import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ExternalLink, MapPinOff } from "lucide-react";
import { useConsent, setConsent } from "@/lib/consent";

const mapSrc = (address) =>
  `https://www.google.com/maps?q=${encodeURIComponent(address)}&z=16&output=embed`;

const mapLink = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

export const LocationMap = ({ cityKey, addresses }) => {
  const [idx, setIdx] = useState(0);
  const consent = useConsent();
  useEffect(() => setIdx(0), [cityKey]);
  const address = addresses[Math.min(idx, addresses.length - 1)];

  return (
    <div className="flex h-full min-h-[380px] flex-col border border-navy/10 bg-white shadow-sm" data-testid="location-map">
      {addresses.length > 1 && (
        <div className="flex border-b border-navy/10" data-testid="location-map-selector">
          {addresses.map((a, i) => (
            <button
              key={a}
              onClick={() => setIdx(i)}
              data-testid={`location-map-address-${i}`}
              className={`relative flex-1 px-3 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors ${
                idx === i ? "text-navy" : "text-slate-400 hover:text-navy"
              }`}
            >
              {a.split(",")[0]}
              {idx === i && (
                <motion.span layoutId="map-selector-underline" className="absolute inset-x-0 -bottom-px h-[2px] bg-gold" />
              )}
            </button>
          ))}
        </div>
      )}

      <div className="relative flex-1 overflow-hidden bg-pearl">
        {consent !== "accepted" ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-navy-texture p-6 text-center" data-testid="location-map-blocked">
            <span className="flex h-12 w-12 items-center justify-center border border-gold/40 bg-gold/10 text-gold">
              <MapPinOff className="h-5 w-5" />
            </span>
            <p className="max-w-xs text-sm leading-relaxed text-slate-300">
              La mappa di Google Maps utilizza cookie di terze parti. Accetta i cookie per visualizzarla oppure apri la sede direttamente su Google Maps.
            </p>
            <button
              onClick={() => setConsent("accepted")}
              data-testid="location-map-accept-cookies"
              className="bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-navy transition-colors hover:bg-gold-light"
            >
              Accetta i cookie e mostra la mappa
            </button>
          </div>
        ) : (
        <AnimatePresence mode="wait">
          <motion.iframe
            key={address}
            title={`Mappa sede ${address}`}
            src={mapSrc(address)}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 h-full w-full border-0"
            data-testid="location-map-iframe"
          />
        </AnimatePresence>
        )}
      </div>

      <a
        href={mapLink(address)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="location-map-directions"
        className="group flex items-center justify-between border-t border-navy/10 bg-navy px-5 py-3.5 text-white"
      >
        <span className="truncate text-sm font-medium">{address}</span>
        <span className="ml-3 flex shrink-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-gold transition-transform group-hover:translate-x-0.5">
          Indicazioni <ExternalLink className="h-3.5 w-3.5" />
        </span>
      </a>
    </div>
  );
};
